import { getAuthenticatedHttpClient } from '@openedx/frontend-base';
import { apiKeys, enableEmailsAction, unenrollmentAction } from '@src/data/services/lms/constants';
import urls from '@src/data/services/lms/urls';
import { stringifyUrl } from '@src/data/services/lms/utils';
import eventNames from '@src/tracking/constants';
import type { PathwayData, PathwaysInCategoryData } from '@src/hooks/usePathwayData';

type PathwaysByCourse = Record<string, PathwayData[]>;

const initializeList = async (user) => {
  const { data } = await getAuthenticatedHttpClient().get(
    stringifyUrl(urls.getInitApiUrl(), { [apiKeys.user]: user }),
  );
  return data;
};

const unenrollFromCourse = async ({ courseId }) => {
  const url = urls.courseUnenroll();
  const formData = new FormData();
  formData.append(apiKeys.courseId, courseId);
  Object.entries(unenrollmentAction).forEach(([key, value]) => {
    formData.append(key, value);
  });
  const response = await getAuthenticatedHttpClient().post(url, formData);
  return response;
};

const updateEntitlementEnrollment = async ({ uuid, courseId }) => {
  const url = urls.entitlementEnrollment(uuid);
  const content = { [apiKeys.courseRunId]: courseId };
  const response = await getAuthenticatedHttpClient().post(url, content);
  return response;
};

const deleteEntitlementEnrollment = async ({ uuid, isRefundable }) => {
  const url = stringifyUrl(
    urls.entitlementEnrollment(uuid),
    { [apiKeys.isRefund]: isRefundable },
  );
  const response = await getAuthenticatedHttpClient().delete(url);
  return response;
};

const updateEmailSettings = async ({ courseId, enable }) => {
  const url = urls.updateEmailSettings();
  const content = { [apiKeys.courseId]: courseId, ...(enable && enableEmailsAction) };
  const response = await getAuthenticatedHttpClient().post(url, content);
  return response;
};

const logEvent = async ({ eventName, data, courseId }) => {
  const url = urls.event();
  const content = {
    courserun_key: courseId,
    event_type: eventName,
    page: window.location.href,
    event: JSON.stringify(data),
  };
  const response = await getAuthenticatedHttpClient().post(url, content);
  return response;
};

const logShare = ({ courseId, site }) => {
  const eventData = {
    eventName: eventNames.shareClicked,
    courseId,
    data: {
      course_id: courseId,
      social_media_site: site,
      location: 'dashboard',
    },
  };
  return logEvent(eventData);
};

const createCreditRequest = async ({ providerId, courseId, username }) => {
  const url = urls.creditRequestUrl(providerId);
  const content = { course_key: courseId, username };
  const response = await getAuthenticatedHttpClient().post(url, content);
  return response;
};

const sendConfirmEmail = async (sendEmailUrl: string) => {
  const response = await getAuthenticatedHttpClient().post(sendEmailUrl);
  return response;
};

// TODO The backend is missing.
// This endpoint should receive a list of course IDs and return a map
// containing the pathways in which the learner is enrolled for each course.
// The response must be mapped here to the `PathwayData` shape.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getPathwaysByCourse = async (_courseIds: string[], _user?: string): Promise<PathwaysByCourse> => ({});

// TODO The backend is missing.
// The pathways must arrive from the backend already grouped by category,
// and the backend needs to return the category label in the plural form,
// already internationalized. The response must be mapped here to the `PathwaysInCategoryData` shape.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getPathwaysByCategory = async (_user?: string): Promise<PathwaysInCategoryData[]> => [];

export {
  initializeList,
  unenrollFromCourse,
  updateEntitlementEnrollment,
  deleteEntitlementEnrollment,
  updateEmailSettings,
  logEvent,
  logShare,
  createCreditRequest,
  sendConfirmEmail,
  getPathwaysByCourse,
  getPathwaysByCategory,
};
